import React, { useCallback, useMemo, useState } from 'react';

import {
  ButtonBase,
  Collapse,
  Grid,
  List,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import {
  gridColumnVisibilityModelSelector,
  GridPanelContent,
  GridPanelFooter,
  GridPanelWrapper,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { matchSorter } from 'match-sorter';
import { AnimatedArrowIcon, ToggleSwitch } from 'src/components/generic';
import { useGetReportColumnOptions } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  createReportGridColDef,
  getColOptionIdentifier,
  getColumnPositionBeforeActions,
  groupByColumnType,
  showColumnAtPosition,
} from './helpers';
import type { ReportGridColDef } from './types';

const useStyles = makeStyles()(theme => ({
  collapseButton: {
    color: theme.appColors.iconsType,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    width: '100%',
  },
  listRoot: {
    maxHeight: 400,
    maxWidth: 300,
    overflow: 'auto',
    position: 'relative',
    width: '100%',
  },
  panel: {
    padding: theme.spacing(2, 0),
  },
  searchField: {
    margin: theme.spacing(1),
  },
  selectAllButtons: {
    ...theme.appTypography.buttonSmall,
    cursor: 'pointer',
  },
  stickyHeader: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    height: 40,
    lineHeight: '40px',
    padding: 0,
  },
  toggleList: {
    padding: 0,
  },
  toggleRow: {
    padding: theme.spacing(1, 2),
  },
  toggleRowText: {
    marginLeft: theme.spacing(2),
    maxWidth: 216,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface Section {
  columns: ReportGridColDef[];
  title: string;
}

export interface ReportColumnPanelProps {
  reportId?: UUID;
}

export function ReportColumnsPanel(_props: ReportColumnPanelProps): JSX.Element | null {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const [search, setSearch] = useState('');
  const { reportColumnOptions } = useGetReportColumnOptions();
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
    [setSearch]
  );
  const toggleAllColumns = React.useCallback(
    (showAllCols: boolean): void => {
      const visibleColumns = reportColumnOptions.reduce<GridColumnVisibilityModel>(
        (acc, colDef) => {
          const identifier = getColOptionIdentifier(colDef);
          acc[identifier] = showAllCols;
          return acc;
        },
        {}
      );

      apiRef.current.setColumnVisibilityModel(visibleColumns);
    },
    [apiRef, reportColumnOptions]
  );
  const sections = useMemo((): Section[] => {
    const keys: (keyof ReportGridColDef)[] = ['field', 'headerName'];
    const matchedColumns = matchSorter<ReportColumnToggleDef>(
      reportColumnOptions.map(o => createReportGridColDef(o)),
      search,
      { keys }
    );
    const { extractionColumns = [], systemColumns = [] } = groupByColumnType(
      [...matchedColumns].sort((a, b) => {
        const aHeaderName = a.headerName ?? '';
        const bHeaderName = b.headerName ?? '';
        return aHeaderName.localeCompare(bHeaderName);
      })
    );

    return [
      {
        columns: systemColumns,
        title: 'System',
      },
      {
        columns: extractionColumns,
        title: 'Questions',
      },
    ];
  }, [reportColumnOptions, search]);

  return (
    <GridPanelWrapper>
      <GridPanelContent>
        <TextField
          className={classes.searchField}
          placeholder="Search columns"
          value={search}
          onChange={handleSearchChange}
        />
        <List className={classes.listRoot} subheader={<li />}>
          {sections.map((section, index) => {
            return <ExpandableSection key={index} section={section} />;
          })}
        </List>
      </GridPanelContent>
      <GridPanelFooter sx={{ p: 1.5 }}>
        <Typography className={classes.selectAllButtons} onClick={() => toggleAllColumns(true)}>
          Select All
        </Typography>
        <Typography className={classes.selectAllButtons} onClick={() => toggleAllColumns(false)}>
          Deselect All
        </Typography>
      </GridPanelFooter>
    </GridPanelWrapper>
  );
}

interface ExpandableSectionProps {
  section: Section;
}

function ExpandableSection({ section }: ExpandableSectionProps): JSX.Element {
  const { classes } = useStyles();
  const [collapseIn, setCollapseIn] = useState<boolean>(true);

  return (
    <li>
      <ul className={classes.toggleList}>
        <ListSubheader className={classes.stickyHeader}>
          <ButtonBase className={classes.collapseButton} onClick={() => setCollapseIn(!collapseIn)}>
            <Typography noWrap variant="body1">
              {section.title}
            </Typography>
            <AnimatedArrowIcon rotated={collapseIn} />
          </ButtonBase>
        </ListSubheader>
        <Collapse in={collapseIn}>
          {section.columns.map((column, index) => {
            return <ReportColumnToggle key={index} colDef={column} />;
          })}
        </Collapse>
      </ul>
    </li>
  );
}

type ReportColumnToggleDef = Omit<ReportGridColDef, 'hide'>;
interface ReportColumnToggleProps {
  colDef: ReportColumnToggleDef;
  disabled?: boolean;
}

function ReportColumnToggle({ colDef, disabled }: ReportColumnToggleProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const visibleColumns = gridColumnVisibilityModelSelector(apiRef);
  const isColVisible = visibleColumns[colDef.field] ?? true;

  return (
    <Grid container className={classes.toggleRow}>
      <ToggleSwitch
        disabled={disabled}
        toggle={() => {
          const newIsColVisible = !isColVisible;

          if (newIsColVisible) {
            const targetIndex = getColumnPositionBeforeActions(apiRef);
            showColumnAtPosition(apiRef, colDef.field, targetIndex);
          } else {
            // For hiding: just hide it
            apiRef.current.setColumnVisibility(colDef.field, false);
          }
        }}
        toggled={isColVisible}
      />
      <Typography className={classes.toggleRowText} variant="body1">
        {colDef.headerName}
      </Typography>
    </Grid>
  );
}
